import React, { useEffect, useMemo, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { TrendingUp, TrendingDown } from "lucide-react";

interface StatsCardProps {
  value: number | null;
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  trend?: { value: number; direction: "up" | "down" };
  gradient?: boolean;
  ariaLabel?: string;
}

export const StatsCard: React.FC<StatsCardProps> = ({
  value,
  label,
  icon: Icon,
  trend,
  gradient,
  ariaLabel,
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [display, setDisplay] = useState(0);

  const duration = 1200; // ms

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || value == null) return;
    let start: number | null = null;
    const startVal = 0;
    const endVal = value;
    const step = (ts: number) => {
      if (start == null) start = ts;
      const progress = Math.min(1, (ts - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setDisplay(Math.round(startVal + (endVal - startVal) * eased));
      if (progress < 1) requestAnimationFrame(step);
    };
    const r = requestAnimationFrame(step);
    return () => cancelAnimationFrame(r);
  }, [inView, value]);

  const formatted = useMemo(() => {
    if (value == null) return "--";
    return new Intl.NumberFormat().format(display);
  }, [display, value]);

  return (
    <Card
      ref={ref}
      role="region"
      aria-label={ariaLabel || label}
      className={cn(
        "group relative overflow-hidden border hover:shadow-glow transition-all duration-300 hover:scale-[1.02]",
        gradient
          ? "bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10"
          : "bg-card"
      )}
    >
      <div className="p-5 flex items-center gap-4">
        <div className="p-3 rounded-xl bg-primary/10 text-primary group-hover:bg-primary/15 transition-colors">
          <Icon className="h-6 w-6 opacity-90" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <div className="text-3xl font-bold leading-tight">{formatted}</div>
          <div className="text-sm text-muted-foreground">{label}</div>
        </div>
        {trend && (
          <div className="ml-auto flex items-center gap-1 text-xs font-medium">
            {trend.direction === "up" ? (
              <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                <TrendingUp className="h-4 w-4" aria-hidden="true" />+{trend.value}%
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400">
                <TrendingDown className="h-4 w-4" aria-hidden="true" />-{trend.value}%
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};
